import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { MapPin, Plane } from "lucide-react";

export const Location = () => {
  const { t } = useLang();
  const titleLines = (t.location.title || "").split("\n");
  return (
    <section
      id="location"
      data-testid="location-section"
      className="relative py-28 md:py-40 bg-forest text-ivory overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-50" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
        >
          <motion.div variants={fadeUp} className="lg:col-span-5">
            <p className="overline text-bronze mb-6">{t.location.eyebrow}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {titleLines.map((l, i) => (
                <span key={i} className="block">
                  {l}
                </span>
              ))}
            </h2>
            <div className="mt-8 text-base md:text-lg leading-relaxed text-ivory/80 space-y-6 max-w-md">
              <p>{t.location.body_1}</p>
              <p>{t.location.body_2}</p>
            </div>
          </motion.div>

          {/* Stylised map card */}
          <motion.div
            variants={fadeUp}
            className="lg:col-span-6 lg:col-start-7 border border-ivory/15 p-8 md:p-10"
            data-testid="location-map"
          >
            <div className="relative flex flex-col gap-16">
              {/* Point A */}
              <div className="relative flex items-start gap-5">
                <span
                  className="relative mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full border border-ivory/30"
                  aria-hidden="true"
                >
                  <Plane size={14} className="text-ivory/80" />
                </span>
                <div>
                  <p className="overline text-ivory/60 mb-1">01</p>
                  <p className="font-serif text-xl md:text-2xl text-ivory">{t.location.point_a}</p>
                </div>
              </div>

              {/* Dotted connector */}
              <div
                aria-hidden="true"
                className="absolute left-[17px] top-12 bottom-12 w-px"
                style={{
                  backgroundImage:
                    "linear-gradient(to bottom, var(--yz-bronze) 33%, rgba(255,255,255,0) 0%)",
                  backgroundPosition: "left",
                  backgroundSize: "1px 12px",
                  backgroundRepeat: "repeat-y",
                }}
              />

              {/* Point B */}
              <div className="relative flex items-start gap-5">
                <span
                  className="relative mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full"
                  style={{ background: "var(--yz-bronze)" }}
                  aria-hidden="true"
                >
                  <MapPin size={14} className="text-ivory" />
                  <span
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{ background: "var(--yz-bronze)", opacity: 0.4 }}
                  />
                </span>
                <div>
                  <p className="overline text-bronze mb-1">02</p>
                  <p className="font-serif text-xl md:text-2xl text-ivory">{t.location.point_b}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-ivory/15 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6">
              <span className="overline text-bronze">{t.location.distance}</span>
              <span className="text-ivory/70 text-sm md:text-base">{t.location.duration}</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
